import { useState, useMemo, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { CodeMirrorEditor } from "./CodeMirrorEditor";
import type { MongoOperation } from "@spuerhund/shared/targets";

export interface MongoOptions {
  projection?: string | undefined;
  sort?: string | undefined;
  skip?: number | undefined;
  limit?: number | undefined;
  hint?: string | undefined;
  maxTimeMS?: number | undefined;
  allowDiskUse?: boolean | undefined;
  collation?: string | undefined;
  advanced?: string | undefined;
}

interface MongoOptionsPaneProps {
  operation: MongoOperation;
  options: MongoOptions;
  onChange: (options: MongoOptions) => void;
  indexNames?: string[];
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">{label}</label>
      <input
        type="number"
        min={0}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder={placeholder ?? "0"}
        className="w-28 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
      />
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">{label}</label>
      <button
        type="button"
        onClick={() => onChange(value ? undefined : true)}
        className={`relative h-5 w-9 rounded-full transition-colors ${value ? "bg-accent" : "bg-zinc-700"}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${value ? "translate-x-4" : ""}`}
        />
      </button>
    </div>
  );
}

function MiniEditor({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-zinc-400">{label}</label>
      <div className="max-h-20 min-h-[2.5rem] overflow-auto rounded border border-zinc-700 bg-surface">
        <CodeMirrorEditor
          value={value ?? ""}
          onChange={onChange}
          {...(placeholder ? { placeholder } : {})}
        />
      </div>
    </div>
  );
}

function HintField({
  value,
  onChange,
  indexNames,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  indexNames: string[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <label className="w-24 shrink-0 text-xs text-zinc-400">hint</label>
      <div className="relative flex-1">
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || undefined)}
          onFocus={() => indexNames.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="index name"
          className="w-full rounded border border-zinc-700 bg-surface px-2 py-1 pr-6 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
        />
        {indexNames.length > 0 && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setOpen(!open)}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
          >
            <ChevronDown size={10} />
          </button>
        )}
        {open && indexNames.length > 0 && (
          <div className="absolute left-0 top-full z-50 mt-1 max-h-40 w-full overflow-auto rounded border border-zinc-700 bg-surface shadow-lg">
            {indexNames.map((name) => (
              <button
                key={name}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                className={`w-full px-2 py-1 text-left font-mono text-xs hover:bg-surface-overlay ${
                  name === value ? "bg-surface-overlay text-zinc-100" : "text-zinc-300"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MongoOptionsPane({ operation, options, onChange, indexNames = [] }: MongoOptionsPaneProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const update = useCallback(
    (patch: Partial<MongoOptions>) => onChange({ ...options, ...patch }),
    [options, onChange],
  );

  const showFind = operation === "find" || operation === "findOne";
  const showAggregate = operation === "aggregate";
  const showPagination = operation === "find";

  // Index names from catalog
  const resolvedIndexNames = useMemo(() => {
    return indexNames.filter((n) => n !== "_id_");
  }, [indexNames]);

  if (operation === "replaceById") {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-xs text-zinc-500">
          replaceById uses <code className="text-zinc-400">_id</code> from the document body.
          No additional options available.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 overflow-auto p-3">
      {/* Projection & Sort (find/findOne) */}
      {showFind && (
        <>
          <MiniEditor
            label="projection"
            value={options.projection}
            onChange={(v) => update({ projection: v })}
            placeholder='{name: 1, email: 1}'
          />
          <MiniEditor
            label="sort"
            value={options.sort}
            onChange={(v) => update({ sort: v })}
            placeholder='{createdAt: -1}'
          />
        </>
      )}

      {/* Pagination (find only, not findOne) */}
      {showPagination && (
        <div className="flex gap-4">
          <NumberField label="skip" value={options.skip} onChange={(v) => update({ skip: v })} />
          <NumberField label="limit" value={options.limit} onChange={(v) => update({ limit: v })} />
        </div>
      )}

      {/* Hint (find + aggregate) */}
      {(showFind || showAggregate) && (
        <HintField value={options.hint} onChange={(v) => update({ hint: v })} indexNames={resolvedIndexNames} />
      )}

      {/* maxTimeMS (find + aggregate) */}
      {(showFind || showAggregate) && (
        <NumberField
          label="maxTimeMS"
          value={options.maxTimeMS}
          onChange={(v) => update({ maxTimeMS: v })}
          placeholder="60000"
        />
      )}

      {/* allowDiskUse (aggregate) */}
      {showAggregate && (
        <ToggleField
          label="allowDiskUse"
          value={options.allowDiskUse}
          onChange={(v) => update({ allowDiskUse: v })}
        />
      )}

      {/* Advanced / raw JSON */}
      <div className="border-t border-zinc-800 pt-2">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300"
        >
          <ChevronDown size={10} className={`transition-transform ${showAdvanced ? "" : "-rotate-90"}`} />
          Advanced options
        </button>
        {showAdvanced && (
          <div className="mt-2">
            <MiniEditor
              label="collation"
              value={options.collation}
              onChange={(v) => update({ collation: v })}
              placeholder='{"locale": "en", "strength": 2}'
            />
            <div className="mt-2">
              <label className="text-xs text-zinc-400">additional options (raw JSON)</label>
              <div className="mt-1 max-h-24 min-h-[2.5rem] overflow-auto rounded border border-zinc-700 bg-surface">
                <CodeMirrorEditor
                  value={options.advanced ?? ""}
                  onChange={(v) => update({ advanced: v })}
                  placeholder="{}"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
